import React from 'react';
import { useLanguage } from '../context/LanguageProvider';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { motion } from 'framer-motion';
import { Camera, Video, Plane, RotateCcw, Building } from 'lucide-react';
import { FallbackUI } from './FallbackUI';

const serviceIcons = [
  <Camera size={32} key="photo" />,
  <Video size={32} key="video" />,
  <Plane size={32} key="drone" />,
  <RotateCcw size={32} key="360" />,
  <Building size={32} key="mgmt" />,
];

export const ServicesSection: React.FC = () => {
  const { t } = useLanguage();
  const { ref, isVisible } = useScrollReveal();

  const items = t.services.items;

  if (!items || items.length === 0) {
    return <FallbackUI message="Services loading..." />;
  }

  return (
    <section id="services" className="relative py-24 md:py-32 bg-vilarias-dark">
      <div className="absolute inset-0 bg-hero-glow opacity-30" />

      <div className="section-padding relative z-10" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="font-serif text-3xl md:text-5xl text-white mb-4">
            {t.services.headline}
          </h2>
          <div className="h-px w-16 bg-vilarias-accent mx-auto" />
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {items.map((item, index) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 30 }}
              animate={isVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.12 }}
              className="group"
            >
              <div className="glass-card rounded-2xl p-8 h-full hover:border-vilarias-accent/40 transition-all duration-500 hover:-translate-y-2 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-vilarias-accent/5 rounded-full blur-3xl group-hover:bg-vilarias-accent/10 transition-all duration-500" />

                <div className="relative z-10">
                  <div className="w-14 h-14 rounded-xl bg-vilarias-accent/10 border border-vilarias-accent/20 flex items-center justify-center text-vilarias-accent mb-5 group-hover:scale-110 group-hover:bg-vilarias-accent/20 transition-all duration-300">
                    {serviceIcons[index] || <Camera size={32} />}
                  </div>

                  <h3 className="text-lg font-medium text-white mb-3 group-hover:text-vilarias-glow transition-colors">
                    {item.title}
                  </h3>

                  <p className="text-sm text-vilarias-text-secondary leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
